/**
 * Публичная страница результата: то, что видит человек, открывший чужую ссылку.
 *
 * Вход не нужен — иначе приглашение превращается в препятствие. Но и ответов
 * здесь нет: получатель в этот квест ещё не играл, и чужой разбор испортил бы
 * ему собственную игру. Сыграть можно только после входа, поэтому кнопка ведёт
 * на страницу квеста, где вход и спросят.
 */
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useSharedResult } from "../api/queries";
import { Avatar } from "../components/Avatar";
import { PlayerLink } from "../components/PlayerLink";
import { ErrorState, ScoreBar, Skeleton } from "../components/ui";
import { formatDate, formatScore } from "../lib/format";
import { useAuthStore } from "../stores/authStore";

export function SharedResultPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const authenticated = useAuthStore((state) => state.status) === "authenticated";

  const shared = useSharedResult(sessionId ?? "", params.get("player") ?? undefined);

  if (shared.isPending) return <Skeleton className="h-96" />;
  if (shared.isError || !shared.data) {
    return <ErrorState message={t("share.notFound")} onRetry={() => void shared.refetch()} />;
  }

  const data = shared.data;
  const score = data.totalScore ?? 0;
  const max = data.maxScore ?? 1;
  const quest = data.quest;

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-4">
      <div className="panel overflow-hidden">
        {quest?.coverImageUrl && (
          <img
            src={quest.coverImageUrl}
            alt=""
            className="h-44 w-full object-cover opacity-80"
          />
        )}

        <div className="space-y-4 p-6 text-center">
          <div className="flex items-center justify-center gap-3">
            <Avatar
              url={data.player?.avatarUrl}
              username={data.player?.username ?? ""}
              role={data.player?.role}
              className="h-11 w-11"
            />
            <div className="text-left">
              <PlayerLink
                userId={data.player?.id}
                username={data.player?.username}
                className="block text-sm text-ink-100"
              />
              <div className="text-xs text-ink-500">{formatDate(data.playedAt)}</div>
            </div>
          </div>

          <div>
            <span className="label">{t("share.playedQuest")}</span>
            <h1 className="font-sans text-3xl text-ink-50">{quest?.title}</h1>
            {quest?.description && (
              <p className="mx-auto mt-2 max-w-lg text-sm text-ink-400">{quest.description}</p>
            )}
          </div>

          <div>
            <div className="font-sans text-5xl text-amber-300">{formatScore(score)}</div>
            <div className="text-xs text-ink-500">
              {t("results.outOf", { max: formatScore(max) })}
            </div>
            <ScoreBar value={score} max={max} className="mt-3" />
          </div>

          {(data.rounds?.length ?? 0) > 0 && (
            <div className="mx-auto max-w-sm space-y-1.5 text-left">
              {data.rounds?.map((round) => (
                <div key={round.roundOrder} className="flex items-center gap-3">
                  <span className="w-14 shrink-0 text-xs text-ink-500">
                    {t("results.roundNumber", { number: round.roundOrder })}
                  </span>
                  <ScoreBar
                    value={round.totalScore ?? 0}
                    max={round.maxScore ?? 1}
                    className="h-1.5 flex-1"
                  />
                  <span className="w-14 shrink-0 text-right tabular-nums text-xs text-amber-300">
                    {formatScore(round.totalScore ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="panel space-y-3 p-6 text-center">
        <h2 className="font-sans text-xl text-ink-50">{t("share.challengeTitle")}</h2>
        <p className="text-sm text-ink-400">
          {authenticated ? t("share.challengeText") : t("share.challengeGuest")}
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          <Link to={`/quests/${quest?.id}`} className="btn-primary">
            {t("share.play")}
          </Link>
          {!authenticated && (
            <Link to="/register" className="btn-secondary">
              {t("nav.register")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
